import { useEffect, useState } from 'react';
import axios from 'axios';
import { getConvertedTotal } from '../api/endpointsBackend';
import { useDisplayCurrency } from '../contexts/DisplayCurrencyContext';

/**
 * MI-298: fetch the single approximate total of a filtered set, converted to the
 * active display currency. When no display currency is selected (or disabled), it
 * is a no-op returning `data: null`, so callers keep rendering the per-currency
 * view unchanged.
 *
 * @param {Array} filters  root filter conditions (same shape as searchMovements)
 * @param {Object} opts    { startDate, endDate, enabled }
 */
export function useConvertedTotal(filters = [], {
  startDate = null,
  endDate = null,
  groups = [],
  groupsOperator = 'and',
  enabled = true,
  // Optional grouping: when set, the response also carries per-bucket converted
  // totals (`buckets`) so a grouped view (e.g. Top Categories) can reuse the same
  // single request as the summary totals, at the same per-movement-date rates.
  dimensions = null,
} = {}) {
  const { displayCurrency } = useDisplayCurrency();
  const [state, setState] = useState({ loading: false, data: null });
  // Stabilise the array/object identities for the effect dependency.
  const requestKey = JSON.stringify([filters || [], groups || [], groupsOperator, dimensions || []]);

  useEffect(() => {
    if (!displayCurrency || !enabled || !filters) {
      setState({ loading: false, data: null });
      return undefined;
    }
    const controller = new AbortController();
    setState({ loading: true, data: null });
    getConvertedTotal(displayCurrency, filters, startDate, endDate, groups, groupsOperator, { signal: controller.signal, dimensions })
      .then((data) => setState({ loading: false, data }))
      .catch((err) => {
        if (!axios.isCancel(err)) setState({ loading: false, data: null });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayCurrency, requestKey, startDate, endDate, enabled]);

  return { displayCurrency, loading: state.loading, data: state.data };
}
